import { useState, useEffect, useCallback, useRef } from 'react';
import SearchableSelect from '@/components/SearchableSelect';
import PageHeader from '@/components/Layout/PageHeader';
import { itemsAPI, soCreateAPI, vendorAPI } from '@/lib/api';
import { SerialHistoryLink } from '@/components/SerialHistoryLink';
import SerialLookup from '@/components/SerialLookup';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus, PencilSimple, Trash, MagnifyingGlass, UploadSimple, DownloadSimple, Package, FunnelSimple,
} from '@phosphor-icons/react';

const CATEGORIES = [
  "Hearing Aid", "Diagnostics", "Accessories", "Sleeves & Domes",
  "Receivers", "Chargers", "Repair", "Moulds", "Service", "Marketing", "Cochlear Implant"
];
const SUB_CATS = ["Basic H", "Basic L", "Essential", "Premium"];
const INV_ACCOUNTS = ["Finished Goods", "Inventory Asset", "Work In Progress"];
const GST_RATES = ["0", "5", "12", "18", "28"];

const EMPTY_FORM = {
  item_type: 'goods', name: '', sku: '', unit: 'PCS', category: 'Hearing Aid',
  sub_category: '', hsn_sac_code: '', product_code: '', brand: '', mrp: '', purchase_price: '',
  tax_preference: 'taxable', intra_state_gst: '18', inter_state_gst: '18', is_tax_inclusive: true,
  track_inventory: false, track_serial_number: false, inventory_account: '', inventory_valuation_method: 'FIFO',
};

const FormField = ({ label, children }) => (
  <div><label className="text-xs text-[#64748B] block mb-1">{label}</label>{children}</div>
);

export default function ItemsMasterPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [invDetail, setInvDetail] = useState(null);
  const [showInvDetail, setShowInvDetail] = useState(false);
  const [invLoading, setInvLoading] = useState(false);
  const [stockForm, setStockForm] = useState({ location: '', location_id: '', rate: '', quantity: 1, serial_numbers: '', vendor_id: '', vendor_name: '' });
  const [stockSaving, setStockSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [vendors, setVendors] = useState([]);
  const fileRef = useRef(null);
  const perPage = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (search) params.search = search;
      if (catFilter) params.category = catFilter;
      if (typeFilter) params.item_type = typeFilter;
      const { data } = await itemsAPI.list(params);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load items'); }
    finally { setLoading(false); }
  }, [page, search, catFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  // Load branches for opening stock location dropdown
  useEffect(() => {
    soCreateAPI.branches().then(r => setBranches(r.data?.branches || [])).catch(() => {});
    vendorAPI.getAll().then(r => setVendors(r.data?.vendors || [])).catch(() => {});
  }, []);

  const openInvDetail = async (item) => {
    setInvLoading(true);
    setShowInvDetail(true);
    try {
      const { data } = await itemsAPI.inventoryDetail(item.id);
      setInvDetail(data);
    } catch { toast.error('Failed to load inventory'); }
    finally { setInvLoading(false); }
  };

  const refreshInvDetail = async () => {
    const { data } = await itemsAPI.inventoryDetail(invDetail.item.id);
    setInvDetail(data);
    load();
  };

  const handleDeleteSerial = async (s) => {
    if (s.status && s.status !== 'available') {
      toast.error(`Serial ${s.serial_number} is consumed (${s.status}) — release it via invoice edit / credit note first.`);
      return;
    }
    if (!window.confirm(`Remove serial "${s.serial_number}" from inventory? This cannot be undone.`)) return;
    try {
      await itemsAPI.deleteSerial(s._uid || s.id || s.serial_number);
      toast.success(`Removed serial ${s.serial_number}`);
      await refreshInvDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove serial'); }
  };

  const [selectedDummies, setSelectedDummies] = useState([]);
  useEffect(() => { setSelectedDummies([]); }, [invDetail?.item?.id]);
  const toggleDummy = (uid) => setSelectedDummies(prev => prev.includes(uid) ? prev.filter(u => u !== uid) : [...prev, uid]);
  const handleRemoveDummy = async () => {
    if (!selectedDummies.length) { toast.error('Tick the dummy serial(s) you want to remove'); return; }
    if (!window.confirm(`Remove ${selectedDummies.length} selected dummy serial(s) from inventory?`)) return;
    try {
      const { data } = await itemsAPI.removeDummySerials(invDetail.item.id, selectedDummies);
      toast.success(`Removed ${data.deleted} dummy serial(s)`);
      setSelectedDummies([]);
      await refreshInvDetail();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove dummy serials'); }
  };

  const handleAddOpeningStock = async () => {
    if (!stockForm.location) { toast.error('Select a location'); return; }
    if (!stockForm.serial_numbers && stockForm.quantity < 1) { toast.error('Enter quantity or serial numbers'); return; }
    setStockSaving(true);
    try {
      const serialsList = stockForm.serial_numbers ? stockForm.serial_numbers.split(',').map(s => s.trim()).filter(Boolean) : [];
      const res = await itemsAPI.addOpeningStock(invDetail.item.id, {
        location: stockForm.location,
        location_id: stockForm.location_id,
        rate: parseFloat(stockForm.rate) || 0,
        quantity: serialsList.length || stockForm.quantity,
        serial_numbers: serialsList,
        vendor_id: stockForm.vendor_id,
        vendor_name: stockForm.vendor_name,
        is_dummy: !!stockForm.is_dummy,
      });
      if (res.data?.added) toast.success(res.data.message || 'Opening stock added');
      else toast.error(res.data?.message || 'Nothing added');
      if (res.data?.skipped) toast.warning(`${res.data.skipped} serial(s) already exist and were skipped — tick "Dummy serials" if these are closing-stock placeholders.`);
      setStockForm({ location: '', location_id: '', rate: '', quantity: 1, serial_numbers: '', vendor_id: '', vendor_name: '', is_dummy: false });
      // Refresh
      const { data } = await itemsAPI.inventoryDetail(invDetail.item.id);
      setInvDetail(data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add stock'); }
    finally { setStockSaving(false); }
  };

  const openCreate = () => { setEditItem(null); setForm({ ...EMPTY_FORM }); setShowForm(true); };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      item_type: item.item_type || 'goods', name: item.name || '', sku: item.sku || '',
      unit: item.unit || 'PCS', category: item.category || 'Hearing Aid',
      sub_category: item.sub_category || '', hsn_sac_code: item.hsn_sac_code || '',
      product_code: item.product_code || '', brand: item.brand || '', mrp: item.mrp || '', purchase_price: item.purchase_price || '',
      tax_preference: item.tax_preference || 'taxable',
      intra_state_gst: item.intra_state_gst || '18', inter_state_gst: item.inter_state_gst || '18',
      is_tax_inclusive: item.is_tax_inclusive !== false,
      track_inventory: item.track_inventory || false, track_serial_number: item.track_serial_number || false,
      inventory_account: item.inventory_account || '', inventory_valuation_method: item.inventory_valuation_method || 'FIFO',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Item name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, mrp: parseFloat(form.mrp) || 0, purchase_price: parseFloat(form.purchase_price) || 0 };
      if (editItem) {
        await itemsAPI.update(editItem.id, payload);
        toast.success('Item updated');
      } else {
        await itemsAPI.create(payload);
        toast.success('Item created');
      }
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await itemsAPI.delete(id); toast.success('Item deleted'); setDeleteConfirm(null); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await itemsAPI.bulkUpload(file);
      // Show a rich summary with counts and the first few errors (if any)
      const parts = [];
      if (data.created != null) parts.push(`${data.created} created`);
      if (data.skipped != null) parts.push(`${data.skipped} duplicates skipped`);
      if (data.error_count) parts.push(`${data.error_count} errors`);
      const summary = parts.length ? parts.join(' · ') : (data.message || 'Upload complete');
      if (data.error_count && data.errors?.length) {
        toast.error(summary, {
          description: data.errors.slice(0, 5).join('\n'),
          duration: 8000,
        });
      } else {
        toast.success(summary);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const fmt = (v) => v ? `₹${Number(v).toLocaleString('en-IN')}` : '-';
  const totalPages = Math.ceil(total / perPage);

  const downloadSample = () => {
    const headers = ["Type","Name","SKU Number","Unit","Brand","Category","Sub-Category","HSN/SAC Code","Product Code","MRP","Purchase Price","Tax Preference","Intra-State GST","Inter-State GST","Track Inventory","Track Serial Number","Inventory Account","Inventory Valuation Method"];
    const sampleRows = [
      ["Goods","Phonak Audeo L90","PHN-L90-001","PCS","Hearing Aid","Premium","9021","PHN-L90","250000","180000","Taxable","12%","12%","Yes","Yes","Finished Goods","FIFO"],
      ["Goods","Signia Styletto 1IX","SIG-1IX-001","PCS","Hearing Aid","Essential","9021","SIG-1IX","149990","100000","Taxable","12%","12%","Yes","Yes","Finished Goods","FIFO"],
      ["Services","Pure Tone Audiometry","SVC-PTA-001","PCS","Diagnostics","","999319","SERVICE-03","700","","Non-Taxable","","","No","No","",""],
    ];
    let csv = headers.join(",") + "\n";
    sampleRows.forEach(r => { csv += r.map(c => c.includes(",") ? `"${c}"` : c).join(",") + "\n"; });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'items_master_sample.csv'; a.click();
    URL.revokeObjectURL(url);
    toast.success('Sample template downloaded');
  };

  return (
    <div className="p-6" data-testid="items-master-page">
      <PageHeader title="Items Master" subtitle="Manage products, services, and inventory items" />

      <div className="mt-6 space-y-4">
        {/* Serial-number lookup — find which device a serial belongs to (#14) */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-[#0F172A]">Find by Serial Number</p>
            <p className="text-xs text-[#64748B]">Search any serial to see the device, its current status &amp; location, and full history.</p>
          </div>
          <SerialLookup />
        </div>

        {/* Actions Bar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search items..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9" data-testid="items-search" />
          </div>
          <Select value={catFilter} onValueChange={v => { setCatFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[160px]" data-testid="items-cat-filter">
              <FunnelSimple size={14} className="mr-1" /><SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={v => { setTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[130px]" data-testid="items-type-filter">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="goods">Goods</SelectItem>
              <SelectItem value="services">Services</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={openCreate} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="add-item-btn">
            <Plus size={16} className="mr-1.5" /> Add Item
          </Button>
          <div className="relative">
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} className="hidden" data-testid="items-upload-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="items-upload-btn">
              <UploadSimple size={16} className="mr-1.5" />{uploading ? 'Uploading...' : 'Bulk Upload'}
            </Button>
          </div>
          <Button variant="outline" onClick={downloadSample} data-testid="download-sample-btn">
            <DownloadSimple size={16} className="mr-1.5" /> Sample File
          </Button>
        </div>

        <p className="text-sm text-[#64748B]">{total} item{total !== 1 ? 's' : ''}</p>

        {/* Table */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="items-table">
            <thead>
              <tr className="bg-[#F8FAFC] text-[#64748B] font-medium text-xs">
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Brand</th>
                <th className="text-left p-3">SKU</th>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">HSN/SAC</th>
                <th className="text-right p-3">MRP</th>
                <th className="text-right p-3">Purchase</th>
                <th className="text-center p-3">GST</th>
                <th className="text-center p-3">Inventory</th>
                <th className="text-center p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="text-center py-8"><div className="inline-block w-5 h-5 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></td></tr>
              ) : items.length > 0 ? items.map(item => (
                <tr key={item.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors cursor-pointer" data-testid={`item-row-${item.id}`} onClick={() => openInvDetail(item)}>
                  <td className="p-3">
                    <div className="font-medium text-[#0F172A]">{item.name}</div>
                    {item.product_code && <div className="text-[10px] text-[#64748B]">{item.product_code}</div>}
                  </td>
                  <td className="p-3 text-xs text-[#64748B]">{item.brand || '-'}</td>
                  <td className="p-3 text-[#64748B] text-xs">{item.sku || '-'}</td>
                  <td className="p-3">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${item.item_type === 'goods' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                      {item.item_type === 'goods' ? 'Goods' : 'Services'}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-[#64748B]">
                    {item.category}{item.sub_category ? ` / ${item.sub_category}` : ''}
                  </td>
                  <td className="p-3 text-xs text-[#64748B]">{item.hsn_sac_code || '-'}</td>
                  <td className="p-3 text-right font-medium">
                    {fmt(item.mrp)}
                    {item.is_tax_inclusive !== false && item.tax_preference === 'taxable' && (
                      <span className="ml-1 text-[9px] bg-emerald-50 text-emerald-700 px-1 rounded" title="MRP is tax-inclusive">incl. tax</span>
                    )}
                  </td>
                  <td className="p-3 text-right text-[#64748B]">{fmt(item.purchase_price)}</td>
                  <td className="p-3 text-center text-xs">
                    {item.tax_preference === 'taxable' ? `${item.intra_state_gst || 18}%` : 'Exempt'}
                  </td>
                  <td className="p-3 text-center">
                    <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded cursor-pointer hover:bg-green-100" onClick={(e) => { e.stopPropagation(); openInvDetail(item); }} data-testid={`inv-badge-${item.id}`}>
                      {item.track_serial_number ? 'Serial' : item.track_inventory ? 'Stock' : 'View'}
                    </span>
                  </td>
                  <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(item)} data-testid={`edit-item-${item.id}`}><PencilSimple size={16} /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteConfirm(item)} data-testid={`delete-item-${item.id}`}><Trash size={16} /></Button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={10} className="text-center py-8 text-[#64748B]">No items found. Click "Add Item" or "Bulk Upload" to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-[#64748B]">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="item-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <Package size={20} />{editItem ? 'Edit Item' : 'Add New Item'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Type */}
            <FormField label="Type *">
              <div className="flex gap-4">
                {['goods', 'services'].map(t => (
                  <label key={t} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="item_type" checked={form.item_type === t} onChange={() => setForm(f => ({ ...f, item_type: t }))} className="accent-[#0F4C81]" data-testid={`type-${t}`} />
                    <span className="text-sm capitalize">{t}</span>
                  </label>
                ))}
              </div>
            </FormField>

            <div className="grid grid-cols-2 gap-4">
              <FormField label="Name *"><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Item name" data-testid="item-name" /></FormField>
              <FormField label="SKU Number"><Input value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} placeholder="SKU-001" data-testid="item-sku" /></FormField>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FormField label="Unit"><Input value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} placeholder="PCS" data-testid="item-unit" /></FormField>
              <FormField label="Product Code"><Input value={form.product_code} onChange={e => setForm(f => ({ ...f, product_code: e.target.value }))} placeholder="Code" data-testid="item-product-code" /></FormField>
              <FormField label="Brand"><Input value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} placeholder="e.g. Phonak, Signia" data-testid="item-brand" /></FormField>
              <FormField label="HSN/SAC Code"><Input value={form.hsn_sac_code} onChange={e => setForm(f => ({ ...f, hsn_sac_code: e.target.value }))} placeholder="9021" data-testid="item-hsn" /></FormField>
            </div>

            {/* Category */}
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Category *">
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v, sub_category: '' }))}>
                  <SelectTrigger data-testid="item-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </FormField>
              {form.category === 'Hearing Aid' && (
                <FormField label="Sub-Category">
                  <Select value={form.sub_category} onValueChange={v => setForm(f => ({ ...f, sub_category: v }))}>
                    <SelectTrigger data-testid="item-sub-category"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>{SUB_CATS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </FormField>
              )}
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-2 gap-4">
              <FormField label="MRP"><Input type="number" value={form.mrp} onChange={e => setForm(f => ({ ...f, mrp: e.target.value }))} placeholder="0" data-testid="item-mrp" /></FormField>
              <FormField label="Purchase Price"><Input type="number" value={form.purchase_price} onChange={e => setForm(f => ({ ...f, purchase_price: e.target.value }))} placeholder="0" data-testid="item-purchase-price" /></FormField>
            </div>

            {/* Tax */}
            <FormField label="Tax Preference *">
              <div className="flex gap-4">
                {[['taxable', 'Taxable'], ['non_taxable', 'Non-Taxable']].map(([v, l]) => (
                  <label key={v} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="tax_pref" checked={form.tax_preference === v} onChange={() => setForm(f => ({ ...f, tax_preference: v }))} className="accent-[#0F4C81]" data-testid={`tax-${v}`} />
                    <span className="text-sm">{l}</span>
                  </label>
                ))}
              </div>
            </FormField>
            {form.tax_preference === 'taxable' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Intra-State GST Rate">
                    <Select value={form.intra_state_gst} onValueChange={v => setForm(f => ({ ...f, intra_state_gst: v }))}>
                      <SelectTrigger data-testid="item-intra-gst"><SelectValue /></SelectTrigger>
                      <SelectContent>{GST_RATES.map(r => <SelectItem key={r} value={r}>{r}%</SelectItem>)}</SelectContent>
                    </Select>
                  </FormField>
                  <FormField label="Inter-State GST Rate">
                    <Select value={form.inter_state_gst} onValueChange={v => setForm(f => ({ ...f, inter_state_gst: v }))}>
                      <SelectTrigger data-testid="item-inter-gst"><SelectValue /></SelectTrigger>
                      <SelectContent>{GST_RATES.map(r => <SelectItem key={r} value={r}>{r}%</SelectItem>)}</SelectContent>
                    </Select>
                  </FormField>
                </div>
                <label className="flex items-center gap-2 cursor-pointer mt-2">
                  <input type="checkbox" checked={form.is_tax_inclusive} onChange={e => setForm(f => ({ ...f, is_tax_inclusive: e.target.checked }))} className="rounded border-gray-300" data-testid="item-tax-inclusive" />
                  <span className="text-sm">MRP is <strong>inclusive</strong> of taxes (recommended)</span>
                </label>
              </>
            )}

            {/* Inventory Tracking */}
            <div className="bg-[#F8FAFC] rounded-lg p-4 space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.track_inventory} onChange={e => setForm(f => ({ ...f, track_inventory: e.target.checked, track_serial_number: false }))} className="rounded border-gray-300" data-testid="item-track-inventory" />
                <span className="text-sm font-medium">Track Inventory for this Item</span>
              </label>
              {form.track_inventory && (
                <>
                  <label className="flex items-center gap-2 cursor-pointer ml-6">
                    <input type="checkbox" checked={form.track_serial_number} onChange={e => setForm(f => ({ ...f, track_serial_number: e.target.checked }))} className="rounded border-gray-300" data-testid="item-track-serial" />
                    <span className="text-sm">Track Serial Number</span>
                  </label>
                  <div className="grid grid-cols-2 gap-4 ml-6">
                    <FormField label="Inventory Account">
                      <Select value={form.inventory_account} onValueChange={v => setForm(f => ({ ...f, inventory_account: v }))}>
                        <SelectTrigger data-testid="item-inv-account"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>{INV_ACCOUNTS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                      </Select>
                    </FormField>
                    <FormField label="Valuation Method">
                      <Input value="FIFO (First In, First Out)" disabled className="bg-gray-100" />
                    </FormField>
                  </div>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="item-save-btn">
              {saving ? 'Saving...' : editItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={o => { if (!o) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-sm" data-testid="delete-item-dialog">
          <DialogHeader><DialogTitle className="font-['Plus_Jakarta_Sans']">Delete Item</DialogTitle></DialogHeader>
          <p className="text-sm text-[#64748B] py-2">Delete <strong>{deleteConfirm?.name}</strong>? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm.id)} data-testid="confirm-delete-item">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inventory Detail Dialog */}
      <Dialog open={showInvDetail} onOpenChange={o => { if (!o) { setShowInvDetail(false); setInvDetail(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="inventory-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <Package size={20} /> {invDetail?.item?.name || 'Item'} — Inventory
            </DialogTitle>
          </DialogHeader>

          {invLoading ? (
            <div className="py-8 text-center"><div className="inline-block w-5 h-5 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></div>
          ) : invDetail && (
            <div className="space-y-5">
              {/* Summary — headline figures from Zoho (authoritative), serial tracking shown separately */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[#EBF3FA] rounded-lg p-3 text-center" data-testid="inv-on-hand">
                  <p className="text-xs text-[#64748B]">On Hand (Zoho)</p>
                  <p className="text-xl font-bold text-[#0F4C81]">{invDetail.zoho_stock_on_hand ?? invDetail.total_stock}</p>
                </div>
                <div className="bg-[#E8F5E9] rounded-lg p-3 text-center" data-testid="inv-available">
                  <p className="text-xs text-[#64748B]">Available (Zoho)</p>
                  <p className="text-xl font-bold text-green-700">{invDetail.total_available}</p>
                </div>
                <div className="bg-[#FFFBEB] rounded-lg p-3 text-center" data-testid="inv-serials">
                  <p className="text-xs text-[#64748B]">Serials Tracked</p>
                  <p className="text-xl font-bold text-[#0F4C81]">{invDetail.serial_total ?? 0}</p>
                  <p className="text-[10px] text-[#64748B] mt-0.5">{invDetail.serial_available ?? 0} avail · {invDetail.serial_sold ?? 0} sold</p>
                </div>
              </div>
              {(invDetail.serial_total ?? 0) > 0 && (invDetail.serial_available ?? 0) !== invDetail.total_available && (
                <p className="text-[11px] text-[#B45309] bg-[#FFFBEB] border border-[#F1F5F9] rounded-md px-3 py-2 -mt-2" data-testid="inv-serial-note">
                  Note: Zoho reports <strong>{invDetail.total_available}</strong> available. Our serial tracking lists {invDetail.serial_available} as available — the difference is unreconciled/legacy serials. The Zoho figure above is authoritative.
                </p>
              )}

              {/* Location-wise Inventory */}
              {invDetail.locations?.length > 0 && (
                <div className="flex justify-end -mb-1">
                  <Button size="sm" variant="outline" onClick={handleRemoveDummy}
                    className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid="remove-dummy-serials-btn">
                    <Trash size={12} className="mr-1" /> Remove Selected Dummies{selectedDummies.length ? ` (${selectedDummies.length})` : ''}
                  </Button>
                </div>
              )}
              {invDetail.locations?.length > 0 ? invDetail.locations.map(loc => (
                <div key={loc.location} className="border border-[#E2E8F0] rounded-lg overflow-hidden">
                  <div className="bg-[#F8FAFC] p-3 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-[#0F172A]">{loc.location}</h4>
                    <div className="flex gap-3 text-xs">
                      <span className="text-green-700">Available: {loc.available}</span>
                      <span className="text-orange-700">Sold: {loc.sold}</span>
                      <span className="text-[#64748B]">Total: {loc.total}</span>
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="flex flex-wrap gap-1.5">
                      {loc.serials.map(s => (
                        <span key={s._uid || s.id || s.serial_number} className="inline-flex items-center gap-1" data-testid={`serial-chip-${s.serial_number}`}>
                          {s.is_dummy && s.status === 'available' && s._uid && (
                            <input type="checkbox" className="h-3 w-3 accent-red-600 cursor-pointer" title="Select to remove"
                              checked={selectedDummies.includes(s._uid)} onChange={() => toggleDummy(s._uid)}
                              data-testid={`select-dummy-${s._uid}`} />
                          )}
                          <SerialHistoryLink
                            serialNumber={s.serial_number}
                            className={s.status === 'available' ? 'bg-green-50 text-green-800 border border-green-200' : s.status === 'sold' ? 'bg-orange-50 text-orange-700 border border-orange-200 line-through' : 'bg-gray-50 text-gray-600 border border-gray-200'}
                          />
                          {s.status === 'available' && (
                            <button
                              onClick={() => handleDeleteSerial(s)}
                              title="Remove serial (only while unused)"
                              className="text-red-400 hover:text-red-600 transition-colors"
                              data-testid={`delete-serial-${s.serial_number}`}>
                              <Trash size={12} />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center py-6 text-[#64748B]">No inventory records found. Add opening stock below.</div>
              )}

              {/* Add Opening Stock Form */}
              <div className="border-2 border-dashed border-[#0F4C81] rounded-lg p-4 bg-[#EBF3FA]">
                <h4 className="text-sm font-semibold text-[#0F4C81] mb-3 flex items-center gap-2">
                  <Plus size={16} /> Add Opening Stock
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#64748B] block mb-1">Location *</label>
                    <SearchableSelect
                      value={stockForm.location}
                      onChange={v => {
                        const br = branches.find(b => b.name === v);
                        setStockForm(f => ({ ...f, location: v, location_id: br?.id || '' }));
                      }}
                      options={branches.map(b => ({ value: b.name, label: b.name }))}
                      placeholder="Select location..." testid="opening-stock-location" buttonClassName="w-full h-9"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#64748B] block mb-1">Rate (Purchase Price)</label>
                    <Input type="number" value={stockForm.rate} onChange={e => setStockForm(f => ({ ...f, rate: e.target.value }))} placeholder="0" data-testid="opening-stock-rate" />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="text-xs text-[#64748B] block mb-1">Vendor (optional)</label>
                  <select
                    value={stockForm.vendor_id}
                    onChange={e => {
                      const v = vendors.find(x => x.vendor_id === e.target.value);
                      setStockForm(f => ({ ...f, vendor_id: e.target.value, vendor_name: v?.name || '' }));
                    }}
                    className="w-full h-9 border border-[#E2E8F0] rounded-md px-2 text-sm"
                    data-testid="opening-stock-vendor"
                  >
                    <option value="">Select vendor (if sourced from a vendor)...</option>
                    {vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.display_name || v.name}</option>)}
                  </select>
                </div>
                <div className="mt-3">
                  <label className="text-xs text-[#64748B] block mb-1">Serial Numbers (comma-separated)</label>
                  <Input value={stockForm.serial_numbers} onChange={e => setStockForm(f => ({ ...f, serial_numbers: e.target.value }))} placeholder="SN-001, SN-002, SN-003" data-testid="opening-stock-serials" />
                  <p className="text-[10px] text-[#64748B] mt-1">Enter serial numbers separated by commas. If no serials, specify quantity below.</p>
                  <label className="mt-2 inline-flex items-center gap-2 text-xs text-[#0F172A] cursor-pointer">
                    <input type="checkbox" checked={!!stockForm.is_dummy} onChange={e => setStockForm(f => ({ ...f, is_dummy: e.target.checked }))} data-testid="opening-stock-dummy" />
                    Dummy serials (closing stock) — leave serials empty to auto-generate one per quantity
                  </label>
                </div>
                {!stockForm.serial_numbers && (
                  <div className="mt-3 w-32">
                    <label className="text-xs text-[#64748B] block mb-1">Quantity</label>
                    <Input type="number" min="1" value={stockForm.quantity} onChange={e => setStockForm(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))} data-testid="opening-stock-qty" />
                  </div>
                )}
                <Button onClick={handleAddOpeningStock} disabled={stockSaving} className="mt-3 bg-[#0F4C81] text-white hover:bg-[#0B3C68]" size="sm" data-testid="opening-stock-submit">
                  {stockSaving ? 'Adding...' : 'Add Stock'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
